import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getThermalScanReport,
  listThermalScans,
  parseTokenClaims,
  ThermalScanRecord,
} from "../../api";
import { Icon } from "../../icons";

export function BorderPage() {
  const { t, i18n } = useTranslation(["dashboard", "common"]);
  parseTokenClaims(); // ensures auth context check via panel
  const [scans, setScans] = useState<ThermalScanRecord[]>([]);
  const [onlyPositive, setOnlyPositive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setError(null);
    try {
      const rows = await listThermalScans({
        person_detected: onlyPositive ? true : undefined,
      });
      setScans(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyPositive]);

  async function onReport(id: string) {
    try {
      const r = await getThermalScanReport(id);
      setInfo(r.summary);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const total = scans.length;
  const positives = scans.filter((s) => s.person_detected).length;

  return (
    <div className="page">
      <div className="page-head-row">
        <div>
          <h2 style={{ marginTop: 0 }}>
            <Icon name="thermometer" /> {t("border.title")}
          </h2>
          <p className="muted">{t("border.intro")}</p>
        </div>
        <label className="row-form">
          <input
            type="checkbox"
            checked={onlyPositive}
            onChange={(e) => setOnlyPositive(e.target.checked)}
          />{" "}
          {t("border.onlyPositive")}
        </label>
      </div>
      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}

      <div className="kpi-grid" style={{ marginTop: "0.5rem" }}>
        <div className="kpi-card tone-indigo">
          <div className="kpi-icon">
            <Icon name="thermometer" size={22} />
          </div>
          <div className="kpi-body">
            <div className="kpi-label">{t("border.totalScans")}</div>
            <div className="kpi-value">{total}</div>
          </div>
        </div>
        <div className={`kpi-card tone-${positives > 0 ? "rose" : "emerald"}`}>
          <div className="kpi-icon">
            <Icon name="alert" size={22} />
          </div>
          <div className="kpi-body">
            <div className="kpi-label">{t("border.personDetection")}</div>
            <div className="kpi-value">{positives}</div>
            <div className="kpi-sub">
              {positives > 0 ? t("border.critical") : t("border.clean")}
            </div>
          </div>
        </div>
      </div>

      <h3 style={{ marginTop: "1.5rem" }}>{t("border.recentScans")}</h3>
      {loading ? (
        <p className="muted">{t("common:actions.loading")}</p>
      ) : scans.length === 0 ? (
        <div className="empty-card">
          <Icon name="thermometer" /> {t("border.emptyScans")}
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t("border.th.time")}</th>
              <th>{t("border.th.vehicle")}</th>
              <th>{t("border.th.location")}</th>
              <th>{t("border.th.result")}</th>
              <th>{t("border.th.maxTemp")}</th>
              <th>{t("border.th.confidence")}</th>
              <th>{t("border.th.alarm")}</th>
              <th>{t("border.th.report")}</th>
            </tr>
          </thead>
          <tbody>
            {scans.map((s) => (
              <tr key={s.id} className={s.person_detected ? "severity-critical" : ""}>
                <td>{new Date(s.scanned_at).toLocaleString(i18n.resolvedLanguage)}</td>
                <td>{s.vehicle_id}</td>
                <td>{s.location_label ?? "—"}</td>
                <td>
                  {s.person_detected ? (
                    <span className="severity-pill sev-critical">{t("border.personDetect")}</span>
                  ) : (
                    <span className="severity-pill sev-medium">{t("border.cleanResult")}</span>
                  )}
                </td>
                <td>{s.max_temp_c ? s.max_temp_c.toFixed(1) : "—"}</td>
                <td>{s.confidence ? `${(s.confidence * 100).toFixed(0)}%` : "—"}</td>
                <td>
                  {s.alert_event_id ? (
                    <code className="small">{s.alert_event_id.slice(0, 14)}…</code>
                  ) : (
                    <span className="muted small">—</span>
                  )}
                </td>
                <td>
                  <button type="button" className="link-button" onClick={() => onReport(s.id)}>
                    {t("border.report")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
